import { useState, useCallback, useEffect } from 'react';
import { getInitialConfig, saveConfig } from '../utils/storage';
import { getDefaultConfig, saveDefaultConfig } from '../utils/api';

/**
 * Custom hook for managing sensor configuration
 * @returns {Object} - Config state and update functions
 */
export const useSensorConfig = () => {
  const [sensorConfig, setSensorConfig] = useState(() => getInitialConfig());
  const [loading, setLoading] = useState(true);

  // On mount, try to load config from API; fallback to local defaults
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getDefaultConfig();
        if (!mounted) return;
        const { thresholds, refreshInterval } = data || {};
        if (thresholds) {
          setSensorConfig({ ...thresholds, refreshInterval: refreshInterval || 1 });
          saveConfig({ ...thresholds, refreshInterval: refreshInterval || 1 });
        }
      } catch (e) {
        // keep local defaults
        console.warn('Falling back to local config. Reason:', e?.message || e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSaveConfig = useCallback(async (newConfig) => {
    const { refreshInterval, ...thresholds } = newConfig || {};
    await saveDefaultConfig({ thresholds, refreshInterval });
    setSensorConfig(newConfig);
    saveConfig(newConfig);
  }, []);

  const updateRefreshInterval = useCallback((interval) => {
    setSensorConfig(prev => {
      const newConfig = { ...prev, refreshInterval: interval };
      saveConfig(newConfig);
      return newConfig;
    });
  }, []);

  return {
    sensorConfig,
    handleSaveConfig,
    updateRefreshInterval,
    refreshInterval: sensorConfig.refreshInterval || 1,
    loading,
  };
};
